import { ProseMirror } from "@progress/kendo-react-editor";

const { TextSelection, InputRule, inputRules, wrappingInputRule, textblockTypeInputRule } = ProseMirror;
/**
 * Function to create input rules for formatting lists.
 * @param {Object} nodes - The nodes object containing list node types.
 * @param {NodeType} nodes.ordered_list - The NodeType for ordered lists.
 * @param {NodeType} nodes.bullet_list - The NodeType for bullet lists.
 * @returns {InputRule} - Returns input rules for formatting lists.
 */
export const inputRule = (nodes) => {
  const { ordered_list, bullet_list } = nodes;
  return inputRules({
    rules: [
      // Converts '- ' or '+ ' to a bullet list.
      wrappingInputRule(/^\s*([-+*])\s$/, bullet_list),
      // Converts '1. ' to an ordered list.
      wrappingInputRule(
        /^(\d+)\.\s$/,
        ordered_list,
        (match) => ({
          order: Number(match[1]),
        }),
        (match, node) => node.childCount + node.attrs.order === match[1]
      ),
      // Converts an URL to a link.
      new InputRule(
        /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})\s$/,
        (state, match, start, end) => {
          const link = state.schema.marks.link.create({
            href: match[1],
          });
          return state.tr.addMark(start, end, link).insertText(" ");
        }
      ),
    ],
  });
};

export const formEditorInputRule = (nodes) => {
  const { ordered_list, bullet_list, heading, blockquote, code_block } = nodes;

  return inputRules({
    rules: [
      // Converts double dashes to an emdash.
      new InputRule(/--$/, "—"),

      // Converts three dots to an ellipsis character.
      new InputRule(/\.\.\.$/, "…"),

      // Converts '# ', '## ', '### ', '#### ', '##### ', '###### '
      // into heading 1, 2, 3, 4, 5, and 6, according to the '#' characters count.
      textblockTypeInputRule(/^(#{1,6})\s$/, heading, (match) => ({ level: match[1].length })),

      // Converts '> ' into a blockquote.
      wrappingInputRule(/^\s*>\s$/, blockquote),

      // Converts three backticks to a code block.
      textblockTypeInputRule(/^```$/, code_block),

      // Converts '- ' or '+ ' to a bullet list.
      wrappingInputRule(/^\s*([-+*])\s$/, bullet_list),

      // Converts '1. ' to an ordered list.
      wrappingInputRule(
        /^(\d+)\.\s$/,
        ordered_list,
        (match) => ({ order: Number(match[1]) }),
        (match, node) => node.childCount + node.attrs.order === match[1]
      ),

      // Converts an URL to a link.
      new InputRule(
        // https://stackoverflow.com/questions/1500260/detect-urls-in-text-with-javascript
        /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})\s$/,
        (state, match, start, end) => {
          let url = match[1];
          if (!/^https?:\/\//i.test(url)) {
            url = "https://" + url; // normalize
          }
          const link = state.schema.marks.link.create({ href: url });
          return state.tr.addMark(start, end, link).insertText(" ");
        }
      ),

      // Converts '!image[https://image-path/something.png]' to image.
      new InputRule(/\!image\[([^\]]+?)\]\s$/, (state, match, start, end) => {
        const image = state.schema.nodes.image.createAndFill({ src: match[1] });
        return image && state.tr.setSelection(TextSelection.create(state.doc, start, end)).replaceSelectionWith(image);
      }),
    ],
  });
};
